import {
  collection,
  doc,
  addDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
  Unsubscribe,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { createPrize, awardPrize, type PrizeType } from './prizes';
import { getRequestsByEventId, updateRequest } from './requests';
import { attendeeDisplayName } from '../utils/attendeeDisplayName';
import { playNextManualOrder } from '../utils/manualQueuePlacement';
import {
  buildRouletteEntrants,
  cryptoRandomIndex,
  pickRouletteCandidate,
  pickRouletteWinner,
  type RouletteCandidate,
  type RouletteVote,
} from '../utils/rouletteDraw';

export const NO_ROULETTE_CANDIDATES = 'NO_ROULETTE_CANDIDATES';

export interface RouletteConfig {
  eventId: string;
  djId: string;
  enabled: boolean;
  mode: 'one_time' | 'recurring';
  intervalMinutes: number;
  prizeType: PrizeType;
  prizeCustomText: string | null;
  allowRepeatWinners: boolean;
  playNext: boolean;
  nextDrawAt: number | null;
  updatedAt: string;
}

export type RouletteConfigValues = Omit<RouletteConfig, 'eventId' | 'djId' | 'updatedAt'>;

export const DEFAULT_ROULETTE_VALUES: RouletteConfigValues = {
  enabled: false,
  mode: 'one_time',
  intervalMinutes: 10,
  prizeType: 'shoutout',
  prizeCustomText: null,
  allowRepeatWinners: false,
  playNext: false,
  nextDrawAt: null,
};

export interface RouletteRound {
  id: string;
  eventId: string;
  djId: string;
  roundNumber: number;
  requestId: string;
  songTitle: string;
  songArtist: string;
  albumArtUrl: string | null;
  entrantIds: string[];
  entrantCount: number;
  winnerId: string;
  winnerNickname: string;
  prizeId: string;
  playNext: boolean;
  drawnAt: string;
  createdAt: string;
}

function docToRouletteConfig(docSnap: DocumentSnapshot): RouletteConfig {
  const data = docSnap.data() ?? {};
  return {
    eventId: data.eventId,
    djId: data.djId,
    enabled: data.enabled === true,
    mode: data.mode === 'recurring' ? 'recurring' : 'one_time',
    intervalMinutes: typeof data.intervalMinutes === 'number' ? data.intervalMinutes : DEFAULT_ROULETTE_VALUES.intervalMinutes,
    prizeType: data.prizeType || DEFAULT_ROULETTE_VALUES.prizeType,
    prizeCustomText: data.prizeCustomText || null,
    allowRepeatWinners: data.allowRepeatWinners === true,
    playNext: data.playNext === true,
    nextDrawAt: typeof data.nextDrawAt === 'number' ? data.nextDrawAt : null,
    updatedAt: data.updatedAt,
  };
}

function docToRouletteRound(docSnap: DocumentSnapshot): RouletteRound {
  const data = docSnap.data() ?? {};
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    roundNumber: data.roundNumber,
    requestId: data.requestId,
    songTitle: data.songTitle,
    songArtist: data.songArtist,
    albumArtUrl: data.albumArtUrl || null,
    entrantIds: Array.isArray(data.entrantIds) ? data.entrantIds : [],
    entrantCount: data.entrantCount ?? 0,
    winnerId: data.winnerId,
    winnerNickname: data.winnerNickname,
    prizeId: data.prizeId,
    playNext: data.playNext === true,
    drawnAt: data.drawnAt,
    createdAt: data.createdAt,
  };
}

export function subscribeToRouletteConfig(
  eventId: string,
  callback: (config: RouletteConfig | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(doc(db, 'rouletteConfigs', eventId), (snapshot) => {
    callback(snapshot.exists() ? docToRouletteConfig(snapshot) : null);
  }, (error) => {
    console.error('[Roulette] Config listener failed:', error);
    callback(null);
    onError?.(error);
  });
}

export async function saveRouletteConfig(eventId: string, djId: string, values: RouletteConfigValues): Promise<void> {
  await setDoc(doc(db, 'rouletteConfigs', eventId), {
    ...values,
    eventId,
    djId,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

// Claims the next recurring draw for this tab. A second DJ tab sees the moved nextDrawAt and skips.
export async function claimRecurringRouletteDraw(eventId: string, nowMs: number): Promise<RouletteConfig | null> {
  const configRef = doc(db, 'rouletteConfigs', eventId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(configRef);
    if (!snapshot.exists()) return null;
    const config = docToRouletteConfig(snapshot);
    if (!config.enabled || config.mode !== 'recurring' || config.nextDrawAt === null || config.nextDrawAt > nowMs) {
      return null;
    }
    const nextDrawAt = nowMs + config.intervalMinutes * 60000;
    transaction.update(configRef, { nextDrawAt, updatedAt: new Date(nowMs).toISOString() });
    return { ...config, nextDrawAt };
  });
}

function rouletteRoundsQuery(eventId: string) {
  return query(collection(db, 'rouletteRounds'), where('eventId', '==', eventId), orderBy('drawnAt', 'desc'));
}

export async function getRouletteRounds(eventId: string): Promise<RouletteRound[]> {
  const snapshot = await getDocs(rouletteRoundsQuery(eventId));
  return snapshot.docs.map(docToRouletteRound);
}

export async function getPastRouletteWinnerIds(eventId: string): Promise<Set<string>> {
  const rounds = await getRouletteRounds(eventId);
  return new Set(rounds.map((round) => round.winnerId).filter(Boolean));
}

export function subscribeToDJRouletteRounds(
  eventId: string,
  callback: (rounds: RouletteRound[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(rouletteRoundsQuery(eventId), (snapshot) => {
    callback(snapshot.docs.map(docToRouletteRound));
  }, (error) => {
    console.error('[Roulette] Rounds listener failed:', error);
    callback([]);
    onError?.(error);
  });
}

export function subscribeToLatestRouletteRound(
  eventId: string,
  callback: (round: RouletteRound | null) => void,
): Unsubscribe {
  const q = query(collection(db, 'rouletteRounds'), where('eventId', '==', eventId), orderBy('drawnAt', 'desc'), limit(1));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.empty ? null : docToRouletteRound(snapshot.docs[0]));
  }, (error) => {
    console.error('[Roulette] Latest round listener failed:', error);
    callback(null);
  });
}

async function getEventVotes(eventId: string): Promise<RouletteVote[]> {
  const snapshot = await getDocs(query(collection(db, 'votes'), where('eventId', '==', eventId)));
  const votes: RouletteVote[] = [];
  for (const voteDoc of snapshot.docs) {
    const data = voteDoc.data();
    const oderId = data.oderId || data.attendeeId;
    if (!data.requestId || !oderId) continue;
    votes.push({
      requestId: data.requestId,
      oderId,
      type: data.type === 'up' || data.type === 'down' ? data.type : undefined,
      nickname: data.nickname || undefined,
    });
  }
  return votes;
}

export async function drawRouletteRound(eventId: string, djId: string, config: RouletteConfigValues): Promise<RouletteRound> {
  const [allRequests, votes, pastRounds] = await Promise.all([
    getRequestsByEventId(eventId),
    getEventVotes(eventId),
    getRouletteRounds(eventId),
  ]);
  const queued = allRequests.filter((request) => request.status === 'queued');

  const excludeIds = new Set<string>([djId]);
  if (!config.allowRepeatWinners) {
    for (const round of pastRounds) excludeIds.add(round.winnerId);
  }

  const candidates: RouletteCandidate[] = queued.map((request) => ({
    requestId: request.id,
    entrants: buildRouletteEntrants(request, votes, excludeIds),
  }));
  const candidate = pickRouletteCandidate(candidates, cryptoRandomIndex);
  if (!candidate) {
    throw new Error(NO_ROULETTE_CANDIDATES);
  }
  const winner = pickRouletteWinner(candidate.entrants, cryptoRandomIndex);
  const song = queued.find((request) => request.id === candidate.requestId)!;

  const roundNumber = pastRounds.length + 1;
  const winnerNickname = attendeeDisplayName(winner.nickname, winner.id);
  const prizeId = await createPrize(eventId, djId, config.prizeType, {
    customText: config.prizeCustomText ?? undefined,
    timeoutMinutes: 5,
    gameSource: 'roulette',
    gameRoundId: `roulette-${roundNumber}`,
  });
  await awardPrize(prizeId, winner.id, winnerNickname);

  if (config.playNext) {
    await updateRequest(song.id, { manualOrder: playNextManualOrder() });
  }

  const now = new Date().toISOString();
  const roundData = {
    eventId,
    djId,
    roundNumber,
    requestId: song.id,
    songTitle: song.song.title,
    songArtist: song.song.artist,
    albumArtUrl: song.song.albumArtUrl || null,
    entrantIds: candidate.entrants.map((entrant) => entrant.id),
    entrantCount: candidate.entrants.length,
    winnerId: winner.id,
    winnerNickname,
    prizeId,
    playNext: config.playNext,
    drawnAt: now,
    createdAt: now,
  };
  const roundRef = await addDoc(collection(db, 'rouletteRounds'), roundData);
  return { id: roundRef.id, ...roundData };
}
